import { useEffect, useMemo } from 'react';

import { JobDetailsList } from 'components/DetailsList/Job/JobDetailsList';
import { PageToolbar } from 'components/PageToolbar/PageToolbar';
import { PrintHidden } from 'components/PrintHidden/PrintHidden';
import { useUser } from 'contexts/UserEnvironmentContext';
import { useSetDocumentTitle } from 'hooks/useSetDocumentTitle';
import type { ApiJob } from 'modules/listing/api/job/types/ApiJob';
import { ListingViewAdminTools } from 'modules/listing/components/ListingView/AdminTools/ListingViewAdminTools';
import { ListingViewBody } from 'modules/listing/components/ListingView/Body/ListingViewBody';
import { ListingViewExpiredTag } from 'modules/listing/components/ListingView/ExpiredTag/ListingViewExpiredTag';
import { ListingViewFooter } from 'modules/listing/components/ListingView/Footer/ListingViewFooter';
import { ListingViewHeader } from 'modules/listing/components/ListingView/Header/ListingViewHeader';
import { ListingViewHeaderSearchContext } from 'modules/listing/components/ListingView/HeaderSearchContext/ListingViewHeaderSearchContext';
import { ListingViewMain } from 'modules/listing/components/ListingView/Main/ListingViewMain';
import { ListingViewSection } from 'modules/listing/components/ListingView/Section/ListingViewSection';
import { ListingViewSignupCta } from 'modules/listing/components/ListingView/SignupCta/ListingViewSignupCta';
import { ListingViewDescriptionSection } from 'modules/listing/components/ListingView/sections/Description/ListingViewDescriptionSection';
import { ListingViewBenefitsSection } from 'modules/listing/components/ListingView/sections/ListingViewBenefitsSection';
import { ListingViewLanguageProficiencySection } from 'modules/listing/components/ListingView/sections/ListingViewLanguageProficiencySection';
import { ListingViewLocationSection } from 'modules/listing/components/ListingView/sections/ListingViewLocationSection';
import { ViewJobActionButtons } from 'modules/listing/jobs/components/ActionButtons/ViewJobActionButtons';
import { ViewJobPageRecommendations } from 'modules/listing/pages/jobs/view/components/Recommendations/ViewJobPageRecommendations';
import { ViewJobPageSidebar } from 'modules/listing/pages/jobs/view/components/Sidebar/ViewJobPageSidebar';
import { SearchContextHeader } from 'modules/search/components/Context/SearchContextHeader';
import { useSearchContextHeader } from 'modules/search/containers/SearchContextHeader/useSearchContextHeader';
import { trackClicked } from 'utils/analytics/track/trackClicked';
import { trackListing } from 'utils/analytics/trackListing';
import { getOrgName } from 'utils/listings/getOrgName';
import { isUserOrgOwner } from 'utils/listings/getOrgOwner';
import { isExternalURL } from 'utils/url/isExternalURL';
import { showModal } from 'zustand-stores/modalStore';
import { dismissToast, showToast } from 'zustand-stores/toastsStore';

type Props = {
  job: ApiJob;
};

export function ExpiredJobPage({ job }: Props) {
  const {
    address,
    benefits,
    benefitsTruncated,
    id,
    hasAts,
    startDate,
    endDate,
    functions,
    areasOfFocus,
    isPostedAnonymously,
    name,
    type,
    description,
    descriptionTruncated,
    remoteOptions,
    url,
    hasOrgNameOverride,
    org,
    orgNameOverride,
    applicationDeadline,
    education,
    isContract,
    isFullTime,
    isTemporary,
    professionalLevel,
    salaryCurrency,
    salaryDetails,
    salaryMaximum,
    salaryMinimum,
    salaryPeriod,
    synonym,
    languageProficiency,
    languageProficiencyTruncated,
    firstPublished,
  } = job;

  useSetDocumentTitle(name);

  const { user } = useUser();
  const isOrgOwner = isUserOrgOwner(user, org?.id);

  const { searchContext } = useSearchContextHeader({ currentObjectId: id });

  const listingDetailsSearchResultsEnabled = useMemo(() => {
    if (isOrgOwner && !searchContext) return false;

    return true;
  }, [isOrgOwner, searchContext]);

  useEffect(() => {
    trackListing({
      status: 'expired',
      hasAts,
      listingName: name,
      listingId: id,
      orgId: org?.id,
      orgName: org?.name,
      orgType: org?.orgType,
      listingType: 'JOB',
    });
  }, [hasAts, id, name, org?.id, org?.name, org?.orgType]);

  useEffect(() => {
    const toastId = showToast({
      headline: getText('This listing has expired'),
      body: getText(
        'This listing has expired and is no longer accepting applications.',
      ),
      dismissible: true,
      variant: 'warning',
    });

    return () => {
      dismissToast(toastId);
    };
  }, []);

  const orgName = getOrgName({ orgNameOverride, isPostedAnonymously, org });

  return (
    <>
      {isOrgOwner && org && (
        <PageToolbar
          centered
          message={getText('Your organization posted this job')}
          tools={
            <ListingViewAdminTools
              listingId={id}
              listingType="JOB"
              orgId={org.id}
              hasAts={hasAts}
              canPost
            />
          }
        />
      )}

      <ListingViewBody>
        <ViewJobPageSidebar
          job={job}
          orgName={orgName}
          listingDetailsSearchResultsEnabled={
            listingDetailsSearchResultsEnabled
          }
          searchContext={searchContext}
          isExpired
        />

        <ListingViewMain>
          {searchContext && (
            <ListingViewHeaderSearchContext searchContext={searchContext} />
          )}

          <ListingViewExpiredTag />

          <ListingViewHeader
            address={address}
            isPostedAnonymously={isPostedAnonymously}
            listingId={id}
            listingName={name}
            listingType={type}
            listingFirstPublished={firstPublished}
            orgName={orgName}
            orgType={org?.orgType}
            orgUrl={hasOrgNameOverride || isPostedAnonymously ? null : org?.url}
            remoteOptions={remoteOptions}
            isExpired
            actionButtons={<ViewJobActionButtons job={job} />}
          />

          <ListingViewSection title={getText('Details')} noBorderTop>
            <JobDetailsList
              orgAreasOfFocus={org?.areasOfFocusKeys}
              startDate={startDate}
              endDate={endDate}
              applicationDeadline={applicationDeadline}
              education={education}
              isContract={isContract}
              isFullTime={isFullTime}
              isTemporary={isTemporary}
              professionalLevel={professionalLevel}
              salaryCurrency={salaryCurrency}
              salaryDetails={salaryDetails}
              salaryMaximum={salaryMaximum}
              salaryMinimum={salaryMinimum}
              salaryPeriod={salaryPeriod}
              jobCountry={address.country}
              areasOfFocus={areasOfFocus}
            />
          </ListingViewSection>

          <ListingViewDescriptionSection
            listingName={name}
            image={null}
            description={description}
            descriptionTruncated={descriptionTruncated}
            handleShowEmailModal={(link) => {
              if (!user && isExternalURL(link.href)) {
                showModal('EMAIL_ALERT', {
                  listingFunctions: functions,
                  orgId: org?.id,
                  listingName: name,
                  listingType: 'JOB',
                  synonym,
                });
              }
            }}
            showMoreLinkTitle={getText('Read more about this job')}
            onToggleDetails={(isOpen) => {
              if (!isOpen) return;

              trackClicked('read more about this listing', {
                listing_type: job.type,
                locale: job.locale,
              });
            }}
          />

          <ListingViewBenefitsSection
            benefits={benefits}
            benefitsTruncated={benefitsTruncated}
            onToggleDetails={(isOpen) => {
              if (!isOpen) return;

              trackClicked('read more about benefits', {
                listing_type: job.type,
                locale: job.locale,
              });
            }}
          />

          <ListingViewLanguageProficiencySection
            languageProficiency={languageProficiency}
            languageProficiencyTruncated={languageProficiencyTruncated}
          />

          <ListingViewLocationSection
            listingType="JOB"
            address={address}
            remoteOptions={remoteOptions}
          />

          {searchContext ? (
            <PrintHidden>
              <div style={{ marginTop: 70 }}>
                <SearchContextHeader searchContext={searchContext} noDivider />
              </div>
            </PrintHidden>
          ) : null}
        </ListingViewMain>
      </ListingViewBody>

      <ListingViewFooter
        hiddenOnDesktop={Boolean(user && listingDetailsSearchResultsEnabled)}
      >
        <ViewJobPageRecommendations
          jobId={id}
          jobTitle={name}
          synonym={synonym}
          functions={job.functions}
          orgAreasOfFocus={org?.areasOfFocusKeys}
        />
        {!user && (
          <ListingViewSignupCta
            listingUrl={url[CURRENT_LOCALE()]}
            type={type}
          />
        )}
      </ListingViewFooter>
    </>
  );
}
